/* eslint-disable @nx/enforce-module-boundaries */
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  ViewChild,
  inject,
  type OnDestroy,
  type Provider,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import {
  getCurrencyByRegion,
  type Currency,
} from '@principle-theorem/accounting';
import {
  AppointmentSchedulingFacade,
  NewAppointmentSaveStatus,
  type NewAppointmentSaveOutcome,
} from '@principle-theorem/ng-appointment/store';
import {
  ChartFacade,
  ChartId,
} from '@principle-theorem/ng-clinical-charting/store';
import { PatientActionsFactoryService } from '@principle-theorem/ng-interactions';
import {
  GlobalStoreService,
  IReasonSelectorValue,
  OrganisationService,
  ScheduleSummaryEventActionsService,
  TagType,
} from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  ConnectedDialogConfig,
  DYNAMIC_COMPONENT_DATA,
  DialogPresets,
  DynamicSidebarService,
  IActionButton,
  TypedFormControl,
  formControlChanges$,
  isValid$,
} from '@principle-theorem/ng-shared';
import {
  AppointmentDeposit,
  Brand,
  ClinicalChart,
  DEPOSIT_REQUIREMENT_RESOLVING_MESSAGE,
  DepositRequirementState,
  DepositSettings,
  IAppointmentDetails,
  IWaitListDetails,
  ScheduleSummary,
  SchedulingEvent,
  SchedulingEventReason,
  StaffDepositBookingContext,
  Staffer,
  TimezoneResolver,
  TreatmentPlanStepPair,
  TreatmentStep,
  isPatientDetails,
  isRequiredAppointmentDetails,
  staffToNamedDocs,
  stafferToNamedDoc,
} from '@principle-theorem/principle-core';
import { AppointmentPermissions } from '@principle-theorem/principle-core/features';
import {
  DEPOSIT_CAPTURE_STRATEGY_TO_PAYMENT_MODE_MAP,
  DepositCaptureStrategy,
  DepositStatus,
  IAppointment,
  ISchedulingEventConditions,
  ISchedulingEventData,
  ISchedulingEventReason,
  SchedulingEventType,
  isUnpaidDepositStatus,
  type DepositPaymentMode,
  type IChecklistItem,
  type IDepositForfeitureDecision,
  type IDepositForfeitureRule,
  type IDepositMetadata,
  type IEvent,
  type IPatient,
  type IPractice,
  type IStaffer,
  type ITag,
} from '@principle-theorem/principle-core/interfaces';
import {
  DATE_FORMAT,
  TIME_FORMAT,
  Timezone,
  filterUndefined,
  isINamedDocument,
  listToSentence,
  shareReplayCold,
  snapshot,
  toDayOfWeek,
  type CollectionReference,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, isNil, isString, omit, omitBy } from 'lodash';
import { type Moment } from 'moment-timezone';
import {
  BehaviorSubject,
  ReplaySubject,
  Subject,
  combineLatest,
  defer,
  of,
  type Observable,
} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
  timeoutWith,
} from 'rxjs/operators';
import { SchedulingScenarioService } from '../../../scheduling-scenario.service';
import { type IWaiveDepositDialogResult } from '../../appointment-view-sidebar/appointment-deposit-sidebar/waive-deposit-dialog/waive-deposit-dialog.component';
import {
  TakeDepositDialog,
  TakeDepositOpenResult,
} from '../../appointment-view-sidebar/take-deposit-dialog';
import { TakeDepositEntry } from '../../appointment-view-sidebar/take-deposit-entry';
import {
  PlannedDepositOutcome,
  PlannedDepositOutcomeKind,
  type IPlannedDepositOutcome,
} from '../../deposit-forfeiture-control/deposit-forfeiture-outcome';
import { AppointmentAutomationsRescheduleComponent } from '../appointment-automations-reschedule/appointment-automations-reschedule.component';
import { AppointmentSelectorDialogComponent } from '../appointment-selector-dialog/appointment-selector-dialog.component';
import {
  AppointmentDepositMetadata,
  DepositStore,
} from '../deposit/deposit.store';
import { AppointmentCreateSidebarService } from './appointment-create-sidebar.service';

export interface IAppointmentCreateSidebarData {
  saveFn: () => Promise<void>;
}

const appointmentSaveOutcomeTimeoutMs = 10_000;
const appointmentSaveTimedOut: NewAppointmentSaveOutcome = {
  status: NewAppointmentSaveStatus.Failed,
};

/**
 * Exported so Storybook can redirect the component-scoped `DepositStore` to a
 * mock via `createComponentProviderOverride`. Storybook's `moduleMetadata`
 * cannot override providers declared inline on the `@Component` decorator.
 */
export const APPOINTMENT_CREATE_SIDEBAR_PROVIDERS: Provider[] = [DepositStore];

@Component({
  selector: 'pr-appointment-create-sidebar',
  templateUrl: './appointment-create-sidebar.component.html',
  styleUrls: ['./appointment-create-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: APPOINTMENT_CREATE_SIDEBAR_PROVIDERS,
  standalone: false,
})
export class AppointmentCreateSidebarComponent implements OnDestroy {
  private _onDestroy$: Subject<void> = new Subject();
  private _global = inject(GlobalStoreService);
  private _previousTabIndex: number = 0;
  readonly depositStore = inject(DepositStore);
  unscheduledErrorMessage = `Appointment cannot be left unscheduled`;

  practices$: Observable<WithRef<IPractice>[]>;
  practitioner$: Observable<INamedDocument<IStaffer>>;
  practitioners$: Observable<INamedDocument<IStaffer>[]>;
  treatmentStepPair$: Observable<TreatmentPlanStepPair | undefined>;
  patientSelectorCtrl = new TypedFormControl<
    WithRef<IPatient> | string | undefined
  >(undefined);

  openTime$: Observable<Moment>;
  closeTime$: Observable<Moment>;

  appointment$: Observable<WithRef<IAppointment> | undefined>;
  patient$: Observable<WithRef<IPatient> | undefined>;
  patientActions: IActionButton[];

  appointmentDetails$: Observable<IAppointmentDetails>;
  waitlistDetails$: Observable<IWaitListDetails>;
  selectedEvent$: Observable<IEvent | undefined>;

  canSave$: Observable<boolean>;
  saving$ = new BehaviorSubject<boolean>(false);
  appointmentTags = new TypedFormControl<INamedDocument<ITag>[]>();
  tagCol$: Observable<CollectionReference<ITag>>;
  automationReschedule$ =
    new ReplaySubject<AppointmentAutomationsRescheduleComponent>(1);

  isOnWaitList$: Observable<boolean>;
  hasAutomations$ = this.automationReschedule$.pipe(
    switchMap((component) => component.hasAutomations$)
  );
  hasTags$: Observable<boolean>;
  validPatientDetails$: Observable<boolean>;
  checklists$: Observable<IChecklistItem[]>;
  hasChecklistItems$: Observable<boolean>;
  newPatientName$ = new BehaviorSubject<string | undefined>(undefined);
  saveDisabledMessage$: Observable<string | undefined>;
  saveButtonTooltip$: Observable<string | undefined>;
  saveButtonLabel$: Observable<string>;
  depositPaymentMode$: Observable<DepositPaymentMode | undefined>;
  canOverrideDepositAmount$: Observable<boolean>;
  currency$: Observable<Currency>;
  forfeitureRules$: Observable<IDepositForfeitureRule[] | undefined>;
  depositAtStake$: Observable<boolean>;
  willForfeitDeposit$: Observable<boolean>;
  depositOutcome$ = new BehaviorSubject<IPlannedDepositOutcome>({
    kind: PlannedDepositOutcomeKind.None,
  });
  depositOutcomeSummary$: Observable<string | undefined>;
  depositForfeitureWarning$: Observable<string | undefined>;
  /**
   * Defaults to settled so a reschedule whose card never renders the
   * forfeiture control is not held back by a lookup that never runs.
   */
  depositForfeitureResolving$ = new BehaviorSubject<boolean>(false);
  /**
   * Defaults to not actionable: a control that never renders cannot be acted
   * on, so the warning must never point staff at its toggle.
   */
  depositActionable$ = new BehaviorSubject<boolean>(false);

  reasonControl = new TypedFormControl<IReasonSelectorValue | undefined>(
    undefined,
    Validators.required
  );
  depositForfeitureControl = new TypedFormControl<
    IDepositForfeitureDecision | undefined
  >();
  timezone$: Observable<Timezone>;
  reasons$: Observable<WithRef<ISchedulingEventReason>[]>;
  schedulingConditions$: Observable<ISchedulingEventConditions>;
  requiresReschedulingReason$: Observable<boolean>;
  showDepositDecision$: Observable<boolean>;
  tagType = TagType.Appointment;

  readonly dateFormat = DATE_FORMAT;
  readonly timeFormat = TIME_FORMAT;

  @ViewChild('mainTabGroup', { read: MatTabGroup }) mainTabGroup?: MatTabGroup;

  @ViewChild(AppointmentAutomationsRescheduleComponent, { static: false })
  set automationReschedule(
    component: AppointmentAutomationsRescheduleComponent
  ) {
    if (component) {
      this.automationReschedule$.next(component);
    }
  }

  constructor(
    private _organisation: OrganisationService,
    private _schedulingFacade: AppointmentSchedulingFacade,
    private _snackBar: MatSnackBar,
    private _dialog: BasicDialogService,
    private _matDialog: MatDialog,
    private _sidebar: DynamicSidebarService,
    private _chart: ChartFacade,
    private _schedulingScenario: SchedulingScenarioService,
    private _appointmentCreateSidebar: AppointmentCreateSidebarService,
    private _scheduleSummaryEvent: ScheduleSummaryEventActionsService,
    actionFactory: PatientActionsFactoryService,
    @Inject(DYNAMIC_COMPONENT_DATA)
    private _data: IAppointmentCreateSidebarData
  ) {
    this.openTime$ = this._schedulingFacade.openTime$;
    this.closeTime$ = this._schedulingFacade.closeTime$;
    this.appointment$ = this._schedulingFacade.currentAppointment$;
    this.patient$ = this._schedulingFacade.selectedPatient$;
    this.appointmentDetails$ = this._schedulingFacade.appointmentDetails$;
    this.waitlistDetails$ = this._schedulingFacade.waitlistDetails$;
    this.selectedEvent$ = this._schedulingFacade.selectedEvent$;
    this.practices$ = this._organisation.practices$;
    this.currency$ = this._organisation.organisation$.pipe(
      filterUndefined(),
      map((organisation) => getCurrencyByRegion(organisation.region))
    );
    this.isOnWaitList$ = this._schedulingFacade.waitlistDetails$.pipe(
      map((waitList) => waitList.addToWaitlist)
    );
    this.hasTags$ = this._schedulingFacade.tags$.pipe(
      map((tags) => tags.length > 0)
    );
    this.hasChecklistItems$ = this._schedulingFacade.checklists$.pipe(
      map((checklists) => checklists.length > 0)
    );
    this.canOverrideDepositAmount$ = this._organisation.userHasPermission$(
      AppointmentPermissions.DepositOverride
    );
    const brandDepositSettings$ = this._schedulingFacade.brand$.pipe(
      map((brand) => brand.settings.onlineBookings?.deposit),
      shareReplayCold()
    );
    this.forfeitureRules$ = brandDepositSettings$.pipe(
      map((settings) =>
        settings
          ? DepositSettings.withDefaults(settings).forfeitureRules
          : undefined
      ),
      shareReplayCold()
    );
    this.depositAtStake$ = this.depositStore.depositMetadata$.pipe(
      map((metadata) => PlannedDepositOutcome.depositAtStake(metadata))
    );
    // Deferred so a late subscriber reads the reason selected so far rather
    // than the empty value `formControlChanges$` captured at construction.
    const selectedReason$ = defer(() =>
      formControlChanges$(this.reasonControl)
    );
    const reasonForfeiturePercentage$ = combineLatest([
      this.forfeitureRules$,
      selectedReason$,
    ]).pipe(
      map(([rules, reasonValue]) =>
        PlannedDepositOutcome.reasonForfeiturePercentage(
          rules,
          reasonValue?.reason
        )
      ),
      shareReplayCold()
    );
    this.depositOutcomeSummary$ = this.depositOutcome$.pipe(
      map((outcome) => PlannedDepositOutcome.summary(outcome))
    );
    this.depositForfeitureWarning$ = combineLatest([
      reasonForfeiturePercentage$,
      this.depositOutcome$,
      this.canOverrideDepositAmount$,
      this.depositAtStake$,
      this.depositActionable$,
    ]).pipe(
      map(([percentage, outcome, canOverride, depositAtStake, canActionHere]) =>
        PlannedDepositOutcome.forfeitureWarning({
          reasonForfeiturePercentage: percentage,
          outcome,
          canOverride,
          depositAtStake,
          canActionHere,
        })
      )
    );
    // The label follows the decision the operator actually made, not the rule
    // the reason suggests, so an overridden waiver never promises a forfeit.
    this.willForfeitDeposit$ = this.depositOutcome$.pipe(
      map((outcome) => outcome.kind !== PlannedDepositOutcomeKind.None)
    );

    this.validPatientDetails$ = this._schedulingFacade.patientDetails$.pipe(
      map((details) => isPatientDetails(details))
    );

    this.practitioners$ = combineLatest([
      this.appointmentDetails$.pipe(map((details) => details.practice)),
      this._schedulingFacade.brand$,
    ]).pipe(
      switchMap(([practice, brand]) =>
        practice
          ? Staffer.practitionersByPractice$(practice)
          : Staffer.practitionersByBrand$(brand)
      ),
      staffToNamedDocs()
    );

    this._organisation.practice$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((practice) => this.updateAppointmentDetails({ practice }));

    this.patientSelectorCtrl.valueChanges
      .pipe(
        filter(
          (patient): patient is WithRef<IPatient> =>
            !isNil(patient) && !isString(patient)
        ),
        takeUntil(this._onDestroy$)
      )
      .subscribe((patient) => this.selectPatient(patient));

    this.patientSelectorCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((name) => {
        if (isString(name)) {
          this.newPatientName$.next(name);
        }
      });

    this.treatmentStepPair$ = this.appointmentDetails$.pipe(
      map((details) => details.treatment),
      shareReplayCold()
    );

    this.checklists$ = this.treatmentStepPair$.pipe(
      switchMap((treatmentStep) =>
        treatmentStep
          ? TreatmentStep.getTreatmentConfigurationChecklists$(
              treatmentStep.step
            )
          : of([])
      )
    );

    this.tagCol$ = this._schedulingFacade.brand$.pipe(
      map((brand) => Brand.appointmentTagCol(brand))
    );

    const depositBookingContext$ = this.patient$.pipe(
      distinctUntilChanged(
        (previous, current) => previous?.ref.path === current?.ref.path
      ),
      switchMap((patient) => StaffDepositBookingContext.resolve$(patient))
    );

    this._schedulingFacade.currentAppointment$
      .pipe(
        filterUndefined(),
        map((appointment) => appointment.deposit?.metadata),
        filterUndefined(),
        takeUntil(this._onDestroy$)
      )
      .subscribe((deposit) => this.depositStore.setDepositMetadata(deposit));

    this.depositStore.evaluateDeposit(
      combineLatest([
        this.patient$,
        this.appointmentDetails$,
        brandDepositSettings$,
        depositBookingContext$,
        this.selectedEvent$,
      ]).pipe(
        map(
          ([
            patient,
            appointmentDetails,
            brandSettings,
            bookingContext,
            selectedEvent,
          ]) => ({
            brandSettings,
            treatmentTemplateId:
              AppointmentDeposit.getTreatmentTemplateId(appointmentDetails),
            treatmentConfigurationIds:
              AppointmentDeposit.getTreatmentConfigurationIds(
                appointmentDetails
              ),
            treatmentConfigurationNames:
              AppointmentDeposit.getTreatmentConfigurationNames(
                appointmentDetails
              ),
            treatmentTemplateName:
              AppointmentDeposit.getTreatmentTemplateName(appointmentDetails),
            treatmentCost:
              AppointmentDeposit.getTreatmentCost(appointmentDetails),
            bookingContext,
            appointmentDay:
              selectedEvent && appointmentDetails.practice
                ? toDayOfWeek(
                    selectedEvent.from,
                    appointmentDetails.practice.settings.timezone
                  )
                : undefined,
            patientDepositRequired: patient?.depositRequired,
            patientName: patient?.name,
          })
        )
      )
    );

    this.appointmentTags.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((value) => this.updateTags(value));

    this._schedulingFacade.currentAppointment$
      .pipe(
        filterUndefined(),
        map((appointment) => appointment.tags),
        takeUntil(this._onDestroy$)
      )
      .subscribe((tags) => {
        this.appointmentTags.setValue(tags, { emitEvent: false });
      });

    this.practitioner$ = this.appointmentDetails$.pipe(
      map((details) =>
        isINamedDocument<IStaffer>(details.practitioner)
          ? details.practitioner
          : undefined
      ),
      filterUndefined()
    );

    this.practitioner$
      .pipe(
        switchMap((practitioner) => this._global.getStaffer$(practitioner.ref)),
        filterUndefined(),
        takeUntil(this._onDestroy$)
      )
      .subscribe((practitioner) =>
        this._chart.setChartingAs(ChartId.InAppointment, practitioner)
      );

    this.patient$
      .pipe(
        filterUndefined(),
        switchMap((patient) =>
          ClinicalChart.getLatestChart$({
            ref: patient.ref,
          })
        ),
        map((chart) => chart ?? ClinicalChart.init()),
        takeUntil(this._onDestroy$)
      )
      .subscribe((chart) =>
        this._chart.loadChartSuccess(ChartId.InAppointment, chart)
      );

    this._appointmentCreateSidebar.triggerPatientSave$
      .pipe(
        filter((save) => save),
        take(1),
        takeUntil(this._onDestroy$)
      )
      .subscribe(() => void this._savePatient());

    this.timezone$ = this._schedulingFacade.brand$.pipe(
      switchMap((brand) => TimezoneResolver.fromBrandRef(brand.ref))
    );

    this.schedulingConditions$ = combineLatest([
      this.timezone$,
      this.appointment$,
      this.selectedEvent$,
    ]).pipe(
      map(([timezone, appointment, selectedEvent]) =>
        SchedulingEvent.getSchedulingConditions(
          timezone,
          appointment?.event?.from,
          selectedEvent?.from
        )
      )
    );
    this.requiresReschedulingReason$ = this.schedulingConditions$.pipe(
      map((conditions) => conditions.eventType !== SchedulingEventType.Schedule)
    );
    /**
     * The two deposit cards answer different questions, but only one of them
     * applies at a time. Provisioning is moot once a deposit exists and has
     * settled, and the decision card repeats its heading and chip, so showing
     * both reads as the same card twice.
     */
    this.showDepositDecision$ = combineLatest([
      this.requiresReschedulingReason$,
      this.depositStore.depositMetadata$,
    ]).pipe(
      map(
        ([requiresReschedulingReason, depositMetadata]) =>
          requiresReschedulingReason && depositMetadata !== undefined
      )
    );

    const reasons$ = this._schedulingFacade.brand$.pipe(
      switchMap((brand) => Brand.cancellationReasons$(brand))
    );
    this.reasons$ = combineLatest([reasons$, this.schedulingConditions$]).pipe(
      map(([reasons, conditions]) =>
        SchedulingEventReason.filterByEventType(reasons, conditions.eventType)
      )
    );

    const hasPatientDetails$ = this._schedulingFacade.patientDetails$.pipe(
      map((details) => isPatientDetails(details))
    );
    const hasAppointmentDetails$ = this.appointmentDetails$.pipe(
      map((details) => isRequiredAppointmentDetails(details))
    );
    const hasRescheduleReason$ = combineLatest([
      this.requiresReschedulingReason$,
      formControlChanges$(this.reasonControl),
    ]).pipe(
      map(([requiresReschedulingReason]) =>
        requiresReschedulingReason ? this.reasonControl.valid : true
      )
    );

    const formValidationData$ = combineLatest([
      hasPatientDetails$,
      hasAppointmentDetails$,
      hasRescheduleReason$,
    ]).pipe(
      map(([hasPatient, hasTreatment, hasRescheduleReason]) => ({
        hasPatient,
        hasTreatment,
        hasRescheduleReason,
      }))
    );

    this.saveDisabledMessage$ = formValidationData$.pipe(
      debounceTime(500),
      map(({ hasPatient, hasTreatment, hasRescheduleReason }) => {
        const missingPatient = !hasPatient ? 'patient details' : undefined;
        const missingTreatment = !hasTreatment
          ? 'treatment details'
          : undefined;
        const missingRescheduleReason = !hasRescheduleReason
          ? 'reschedule reason'
          : undefined;
        const missing = compact([
          missingPatient,
          missingTreatment,
          missingRescheduleReason,
        ]);
        return missing.length
          ? `Missing ${listToSentence(missing)}`
          : undefined;
      })
    );

    const actionPatient$ = this.patient$.pipe(filterUndefined());
    this.patientActions = [
      actionFactory.sms(actionPatient$),
      actionFactory.call(actionPatient$),
      actionFactory.email(actionPatient$),
    ];

    // Deferred for the same reason as `selectedReason$`: the control may
    // already hold a decision by the time anything subscribes.
    const depositForfeitureValid$ = defer(() =>
      isValid$(this.depositForfeitureControl)
    ).pipe(distinctUntilChanged());
    const formValid$ = combineLatest([
      formValidationData$,
      depositForfeitureValid$,
      this.depositForfeitureResolving$,
    ]).pipe(
      map(
        ([
          { hasPatient, hasTreatment, hasRescheduleReason },
          depositForfeitureValid,
          depositForfeitureResolving,
        ]) =>
          hasPatient &&
          hasTreatment &&
          hasRescheduleReason &&
          depositForfeitureValid &&
          !depositForfeitureResolving
      )
    );

    this.canSave$ = AppointmentDeposit.getCanSave$({
      savingAppointment$: this._schedulingFacade.savingAppointment$,
      formValid$,
      depositRequirement$: this.depositStore.depositRequirement$,
      depositMetadata$: this.depositStore.depositMetadata$,
      depositAmount$: this.depositStore.depositAmount$,
      depositRequiredOnlyBecauseStaffOptedIn$:
        this.depositStore.depositRequiredOnlyBecauseStaffOptedIn$,
    });

    const depositSaveDisabledMessage$ =
      AppointmentDeposit.getDepositSaveDisabledMessage$({
        depositRequirement$: this.depositStore.depositRequirement$,
        depositMetadata$: this.depositStore.depositMetadata$,
        depositAmount$: this.depositStore.depositAmount$,
        depositRequiredOnlyBecauseStaffOptedIn$:
          this.depositStore.depositRequiredOnlyBecauseStaffOptedIn$,
      });
    // Saving mid-lookup writes a scheduling event with no forfeiture
    // decision, so the cancellation fee is never charged.
    this.saveButtonTooltip$ = combineLatest([
      depositSaveDisabledMessage$,
      this.depositForfeitureResolving$,
    ]).pipe(
      map(([depositMessage, depositForfeitureResolving]) =>
        depositForfeitureResolving
          ? DEPOSIT_REQUIREMENT_RESOLVING_MESSAGE
          : depositMessage
      )
    );
    this.saveButtonLabel$ = AppointmentDeposit.getSaveButtonLabel$({
      depositRequirement$: this.depositStore.depositRequirement$,
      depositMetadata$: this.depositStore.depositMetadata$,
      selectedEvent$: this.selectedEvent$,
      willForfeitDeposit$: this.willForfeitDeposit$,
    });
    this.depositPaymentMode$ = AppointmentDeposit.getEffectiveCapture$({
      captureStrategy$: this.depositStore.depositCaptureStrategy$,
      selectedEvent$: this.selectedEvent$,
      appointmentRequest$: this._schedulingFacade.appointmentRequest$,
    }).pipe(map((capture) => capture.paymentMode));

    this.depositStore.appointmentRequest$
      .pipe(filterUndefined(), takeUntil(this._onDestroy$))
      .subscribe((appointmentRequest) =>
        this._schedulingFacade.setAppointmentRequest(appointmentRequest)
      );
  }

  ngOnDestroy(): void {
    this._schedulingFacade.reset();
    this._appointmentCreateSidebar.reset();
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  selectPatient(patient: WithRef<IPatient>): void {
    this._schedulingFacade.selectPatient(patient);
  }

  depositOutcomeChanged(outcome: IPlannedDepositOutcome): void {
    this.depositOutcome$.next(outcome);
  }

  depositForfeitureResolvingChanged(resolving: boolean): void {
    this.depositForfeitureResolving$.next(resolving);
  }

  depositActionableChanged(actionable: boolean): void {
    this.depositActionable$.next(actionable);
  }

  clearPatient(): void {
    const patientName = this.patientSelectorCtrl.value;
    this.patientSelectorCtrl.reset();
    if (isString(patientName)) {
      this.newPatientName$.next(patientName);
    }
    this._schedulingFacade.clearPatient();
  }

  updateAppointmentDetails(details: Partial<IAppointmentDetails>): void {
    details = omitBy(details, isNil);
    this._schedulingFacade.appointmentFormChange(details);
  }

  updateWaitList(details: IWaitListDetails): void {
    this._schedulingFacade.updateWaitListDetails(details);
  }

  updateChecklists(checklists: IChecklistItem[]): void {
    this._schedulingFacade.updateChecklists(checklists);
  }

  updateTags(tags: INamedDocument<ITag>[]): void {
    this._schedulingFacade.updateTags(tags);
  }

  amountOverridden(amount: number): void {
    this.depositStore.overrideAmount(amount);
  }

  amountResetToDefault(): void {
    this.depositStore.resetAmountToDefault();
  }

  async waiveDeposit(result: IWaiveDepositDialogResult): Promise<void> {
    const [
      resolvedCaptureStrategy,
      amount,
      staffer,
      treatmentConfigurationDeposits,
      currency,
      depositOrigin,
    ] = await Promise.all([
      snapshot(this.depositStore.depositCaptureStrategy$),
      snapshot(this.depositStore.depositAmount$),
      snapshot(this._organisation.staffer$.pipe(filterUndefined())),
      snapshot(this.depositStore.treatmentConfigurationDeposits$),
      snapshot(this.currency$),
      snapshot(this.depositStore.depositOrigin$),
    ]);

    if (amount === undefined) {
      return;
    }

    const captureStrategy = await this._getDepositCaptureStrategy(
      resolvedCaptureStrategy
    );

    this.depositStore.setDepositMetadata({
      status: DepositStatus.Waived,
      amountCents: amount,
      currency,
      paymentMode:
        DEPOSIT_CAPTURE_STRATEGY_TO_PAYMENT_MODE_MAP[captureStrategy],
      ...depositOrigin,
      ...(AppointmentDepositMetadata.treatmentConfigurationDepositsForAmount(
        amount,
        treatmentConfigurationDeposits
      )
        ? { treatmentConfigurationDeposits }
        : {}),
      waivedBy: stafferToNamedDoc(staffer),
      waiverReason: result.waiverReason,
    });
  }

  tabChanged(event: MatTabChangeEvent): void {
    const patientTabIndex = 2;
    const currentTabIndex = event.index;

    if (
      this._previousTabIndex === patientTabIndex &&
      this._previousTabIndex !== currentTabIndex
    ) {
      this._schedulingFacade.savePatient();
      this._appointmentCreateSidebar.clearPatientDetailsChanges();
    }
    this._previousTabIndex = currentTabIndex;
  }

  async save(): Promise<void> {
    if (this.depositForfeitureControl.invalid) {
      this.depositForfeitureControl.markAsTouched();
      return;
    }
    const canSave = await snapshot(this.canSave$);
    if (!canSave) {
      return;
    }
    this.saving$.next(true);
    await this._savePatient();
    await this._saveAppointment();
    await this._data.saveFn();
    this.saving$.next(false);
  }

  showAppointmentOptions(connectedTo: HTMLElement): void {
    this._dialog.connected(
      AppointmentSelectorDialogComponent,
      this._getAppointmentOptionsDialogConfig(connectedTo)
    );
  }

  createNewPatient(): void {
    if (!this.mainTabGroup) {
      return;
    }
    this.clearPatient();
    this.mainTabGroup.selectedIndex = 2;
  }

  private _openUnscheduledMessage(): void {
    this._snackBar.open(this.unscheduledErrorMessage, '', {
      duration: 5000,
    });
  }

  private _getAppointmentOptionsDialogConfig(
    connectedTo: HTMLElement
  ): ConnectedDialogConfig {
    return {
      ...DialogPresets.large(),
      connectedTo: new ElementRef(connectedTo),
      positions: [
        {
          originX: 'start',
          originY: 'center',
          overlayX: 'end',
          overlayY: 'center',
          offsetX: -40,
          offsetY: 100,
        },
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'center',
          offsetX: -40,
          offsetY: 100,
        },
      ],
    };
  }

  private async _savePatient(): Promise<void> {
    this._schedulingFacade.savePatient();
    await this._schedulingFacade.savingPatient$
      .pipe(
        filter((saving) => !saving),
        take(1)
      )
      .toPromise();
  }

  private async _saveAppointment(): Promise<void> {
    const appointment = await snapshot(this.appointment$);
    const selectedEvent = await snapshot(this._schedulingFacade.selectedEvent$);
    const appointmentDetails = await snapshot(
      this._schedulingFacade.appointmentDetails$
    );
    const [depositMetadata, depositRequirement, depositAmount] =
      await Promise.all([
        snapshot(this.depositStore.depositMetadata$),
        snapshot(this.depositStore.depositRequirement$),
        snapshot(this.depositStore.depositAmount$),
      ]);
    if (depositRequirement === DepositRequirementState.Resolving) {
      return;
    }
    const appointmentDeposit = await this._getAppointmentDepositMetadata(
      depositMetadata,
      depositRequirement,
      depositAmount
    );
    const treatmentCategory = appointmentDetails.treatment
      ? TreatmentStep.defaultDisplayRef(
          appointmentDetails.treatment.step.display
        )
      : undefined;

    if (selectedEvent) {
      const isBlockedByDoubleBooking =
        await this._schedulingScenario.isBlockedByDoubleBooking(
          {
            event: selectedEvent,
            ref: appointment?.ref,
          },
          treatmentCategory
        );
      if (isBlockedByDoubleBooking) {
        return;
      }
    }

    const automationRescheduleComponent = await snapshot(
      this.automationReschedule$
    );
    await automationRescheduleComponent.update();

    const practice = await snapshot(
      this._organisation.practice$.pipe(filterUndefined())
    );
    const schedulingEventData: ISchedulingEventData = {
      scheduledByPractice: practice.ref,
      reasonSetManually: false,
      schedulingConditions: await snapshot(this.schedulingConditions$),
    };

    if (!appointment) {
      this._schedulingFacade.saveNewAppointment(
        schedulingEventData,
        appointmentDeposit
      );
      const outcome = await this._newAppointmentSaveOutcome();
      if (!outcome) {
        return;
      }
      if (
        outcome.status !== NewAppointmentSaveStatus.Saved ||
        !appointmentDeposit ||
        !isUnpaidDepositStatus(appointmentDeposit.status)
      ) {
        this._sidebar.close(true);
        return;
      }
      // Handing the close to the open call defers it until the dialog is
      // certain to appear, so the operator never watches this sidebar shut on
      // a capture that then fails to start.
      const openResult = await TakeDepositDialog.open(
        this._matDialog,
        outcome.appointment,
        TakeDepositEntry.Booking,
        () => this._sidebar.close(true)
      );
      // `beforeOpen` only runs when the dialog actually opens, so a saved
      // appointment that cannot reach capture would otherwise leave the create
      // sidebar sitting open over a completed save.
      if (openResult !== TakeDepositOpenResult.Opened) {
        this._sidebar.close(true);
      }
      return;
    }

    const event = await snapshot(this.selectedEvent$);
    if (!event) {
      this._openUnscheduledMessage();
      return;
    }

    const summary = await ScheduleSummary.getSummaryFromEventable({
      ...appointment,
      event,
      ref: appointment.ref,
    });
    this._scheduleSummaryEvent.setSelectedSummary(summary);

    const reasonFormData = this.reasonControl.value;

    this._schedulingFacade.rescheduleAppointment(
      {
        ...schedulingEventData,
        reason: reasonFormData?.reason ?? undefined,
        reasonSetManually: reasonFormData?.reasonSetManually ?? false,
        depositForfeiture: this.depositForfeitureControl.value ?? undefined,
      },
      appointmentDeposit
    );
    this._sidebar.close(true);
  }

  /**
   * A save the effect cannot start, such as one whose new patient failed to
   * save, ends without any outcome, so the wait is capped and the silence
   * treated as a failure. Resolves `undefined` once the sidebar is destroyed,
   * leaving nothing more to do.
   */
  private async _newAppointmentSaveOutcome(): Promise<
    NewAppointmentSaveOutcome | undefined
  > {
    return snapshot(
      this._schedulingFacade.newAppointmentSaveOutcome$.pipe(
        filterUndefined(),
        timeoutWith(
          appointmentSaveOutcomeTimeoutMs,
          of(appointmentSaveTimedOut)
        ),
        takeUntil(this._onDestroy$)
      )
    );
  }

  private async _getDepositCaptureStrategy(
    resolvedCaptureStrategy?: DepositCaptureStrategy
  ): Promise<DepositCaptureStrategy> {
    const brand = await snapshot(this._schedulingFacade.brand$);
    return (
      resolvedCaptureStrategy ??
      brand.settings.onlineBookings?.deposit?.captureStrategy ??
      DepositCaptureStrategy.ChargeOnBookingRequest
    );
  }

  private async _getAppointmentDepositMetadata(
    depositMetadata: IDepositMetadata | undefined,
    depositRequirement: DepositRequirementState,
    depositAmount?: number
  ): Promise<IDepositMetadata | undefined> {
    // Accepting a booking request is the action a flagged deposit was waiting
    // on: the money now funds a real appointment. Carrying the flag across
    // would leave a warning on the appointment that nothing ever clears.
    const appointmentRequest = await snapshot(
      this.depositStore.appointmentRequest$
    );
    if (depositMetadata) {
      return appointmentRequest
        ? omit(depositMetadata, 'attention')
        : depositMetadata;
    }

    const linkedDepositRequestMetadata =
      await this._getLinkedDepositRequestMetadata();
    if (linkedDepositRequestMetadata) {
      return omit(linkedDepositRequestMetadata, 'attention');
    }

    switch (depositRequirement) {
      case DepositRequirementState.Required:
        break;
      case DepositRequirementState.Resolving:
      case DepositRequirementState.NotRequired:
      default:
        return;
    }

    const [
      captureStrategy,
      treatmentConfigurationDeposits,
      currency,
      depositOrigin,
    ] = await Promise.all([
      snapshot(this.depositStore.depositCaptureStrategy$),
      snapshot(this.depositStore.treatmentConfigurationDeposits$),
      snapshot(this.currency$),
      snapshot(this.depositStore.depositOrigin$),
    ]);
    const effectiveCaptureStrategy =
      await this._getDepositCaptureStrategy(captureStrategy);

    return {
      status: DepositStatus.Required,
      amountCents: depositAmount ?? 0,
      currency,
      ...depositOrigin,
      paymentMode:
        DEPOSIT_CAPTURE_STRATEGY_TO_PAYMENT_MODE_MAP[effectiveCaptureStrategy],
      captureStrategy: effectiveCaptureStrategy,
      ...(depositAmount !== undefined &&
      AppointmentDepositMetadata.treatmentConfigurationDepositsForAmount(
        depositAmount,
        treatmentConfigurationDeposits
      )
        ? { treatmentConfigurationDeposits }
        : {}),
    };
  }

  private async _getLinkedDepositRequestMetadata(): Promise<
    IDepositMetadata | undefined
  > {
    const [appointmentRequest, depositRequest] = await Promise.all([
      snapshot(this.depositStore.appointmentRequest$),
      snapshot(this.depositStore.depositRequest$),
    ]);

    return DepositStore.metadataFromLinkedDepositRequest(
      appointmentRequest,
      depositRequest
    );
  }
}
